import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Link,
  useLocation,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import {
  Badge,
  Button,
  ConfirmationDialog,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@roger/ui-components';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';
import { useStore } from '../store';
import { PermissionSlug } from '../constants/featureFlags';
import CreateIntakeDialog from '../components/faxInbox/CreateIntakeDialog';
import FaxPagesSection from '../components/faxInbox/FaxPagesSection';
import FaxRoutePanel from '../components/faxInbox/FaxRoutePanel';
import SenderLabel from '../components/faxInbox/SenderLabel';
import { displayName } from '../components/faxInbox/faxItemDisplay';
import { useProcessingPoll } from '../components/faxInbox/useProcessingPoll';
import { LEAVE_GUARD_SKIP, useLeaveGuard } from '../hooks/useLeaveGuard';
import {
  filtersToQuery,
  pageOf,
  readFilters,
  readPage,
  UUID,
  writeFilters,
  type ReviewNavState,
} from '../components/faxInbox/inboxFilters';
import {
  NONE,
  useFaxItemEditor,
} from '../components/faxInbox/useFaxItemEditor';
import { removeFaxItem } from '../services/faxClassification';
import {
  getFaxItem,
  getFaxItemNeighbors,
  type FaxItem,
  type FaxItemIntakeStarted,
  type FaxItemKind,
  type FaxItemNeighbors,
  type FaxItemRef,
} from '../services/faxItems';
import { routeFaxItem, type RouteFaxRequest } from '../services/faxRouting';
import {
  getBusinessErrorMessage,
  getErrorConflictCode,
} from '../utils/apiErrorDetail';
import { getHttpStatus } from '../utils/httpErrorUtils';
import { formatDateTimeInPT } from '../utils/dateUtils';
import { OrderBadge, StatusBadge } from './FaxInbox';

const KINDS: FaxItemKind[] = ['arrival', 'document'];

function withSearch(path: string, search: string): string {
  return search ? `${path}?${search}` : path;
}

/**
 * One fax, large, with where it goes beside it.
 *
 * Previous and Next walk the list as it was filtered, through the server's
 * own order, and replace history so browser Back always lands on the list.
 * After filing, archiving or removing, the next fax opens.
 */
export default function FaxReview() {
  const params = useParams();
  const kindParam = params.kind;
  // The server answers in lowercase, so an uppercase link still matches.
  const itemId = (params.itemId ?? '').toLowerCase();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navState = (location.state ?? {}) as ReviewNavState;
  const { showError, showSuccess, hasPermission, hasProduct, rogerEmr } =
    useStore(
      useShallow(state => ({
        showError: state.showError,
        showSuccess: state.showSuccess,
        hasPermission: state.hasPermission,
        hasProduct: state.hasProduct,
        rogerEmr: state.user?.agency?.is_rogeremr_enabled === true,
      }))
    );
  const canManage =
    hasProduct('fax') && hasPermission(PermissionSlug.FAX_INBOX_MANAGE);
  const canIntake = hasPermission(PermissionSlug.INTAKE_ACCESS);

  // Null for any URL that is not a real fax ref, so no request is made and a
  // crafted id never reaches another API path.
  const kind =
    KINDS.includes(kindParam as FaxItemKind) && UUID.test(itemId)
      ? (kindParam as FaxItemKind)
      : null;
  const search = searchParams.toString();
  const filters = useMemo(
    () => readFilters(new URLSearchParams(search)),
    [search]
  );
  const urlPage = readPage(new URLSearchParams(search));

  const [item, setItem] = useState<FaxItem | null>(null);
  const [loadState, setLoadState] = useState<
    'loading' | 'ready' | 'missing' | 'failed'
  >('loading');
  const [neighbors, setNeighbors] = useState<FaxItemNeighbors | null>(
    navState.neighbors ?? null
  );
  const [busy, setBusy] = useState(false);
  const [filing, setFiling] = useState(false);
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  const [pending, setPending] = useState<(() => void) | null>(null);
  const [intakeOpen, setIntakeOpen] = useState(false);
  const loadGeneration = useRef(0);
  // The fax the URL names now. An action that finished after the URL moved
  // must not reload, advance or navigate on behalf of the fax it started on.
  const urlKey = `${kindParam}:${itemId}`;
  const currentUrl = useRef<string | null>(urlKey);
  // Declared before the load effect so it runs first; null once the page
  // unmounts, so an action finishing after the user left does nothing.
  useEffect(() => {
    currentUrl.current = urlKey;
    return () => {
      currentUrl.current = null;
    };
  }, [urlKey]);
  const stillAt = (key: string) => currentUrl.current === key;

  const loadItem = useCallback(
    async ({
      silent = false,
      isCurrent = () => true,
    }: { silent?: boolean; isCurrent?: () => boolean } = {}) => {
      if (!canManage) return;
      if (!kind) {
        setItem(null);
        setLoadState('missing');
        return;
      }
      // A load for a fax the URL has left would cancel the current one.
      if (currentUrl.current !== `${kind}:${itemId}`) return;
      // A background refresh takes no generation, so any load asked for wins.
      const generation = silent
        ? loadGeneration.current
        : ++loadGeneration.current;
      try {
        const found = await getFaxItem(kind, itemId);
        if (generation !== loadGeneration.current || !isCurrent()) return;
        setItem(found);
        setLoadState('ready');
      } catch (error) {
        if (generation !== loadGeneration.current) return;
        if (silent) {
          console.warn('Background refresh of the fax failed', error);
          return;
        }
        setItem(null);
        if (getHttpStatus(error) === 404) {
          setLoadState('missing');
        } else {
          console.error('Failed to load the fax', error);
          showError('Could not load that fax.');
          setLoadState('failed');
        }
      }
    },
    [canManage, kind, itemId, showError]
  );

  // A self-replace keeps the fax on screen while its new row loads; any
  // other move clears it, so nothing from the last fax is shown or seeded.
  const selfReplace = useRef(false);
  selfReplace.current =
    !!navState.origin && navState.origin !== `${kindParam}:${itemId}`;
  useEffect(() => {
    if (!selfReplace.current) {
      setItem(null);
      setLoadState('loading');
    }
    void loadItem();
  }, [loadItem]);

  // Held neighbors come with an in-page replace, so a fax that became a
  // document keeps its place. Anything else asks the server.
  const heldNeighbors = navState.neighbors;
  useEffect(() => {
    if (!canManage || !kind) return;
    if (heldNeighbors) {
      setNeighbors(heldNeighbors);
      return;
    }
    let cancelled = false;
    setNeighbors(null);
    getFaxItemNeighbors(kind, itemId, filtersToQuery(filters))
      .then(found => {
        if (!cancelled) setNeighbors(found);
      })
      .catch(error => {
        // A missing fax is already said by the item load.
        if (!cancelled && getHttpStatus(error) !== 404) {
          console.error('Failed to load the neighbors', error);
          showError('Could not load the next and previous faxes.');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [canManage, kind, itemId, filters, heldNeighbors, showError]);

  const onConflict = useCallback(() => {
    showError(
      'Somebody else changed that document. It has been reloaded, so please try again.'
    );
    void loadItem();
  }, [loadItem, showError]);

  const editor = useFaxItemEditor(item, { onError: showError, onConflict });
  const { dirty } = editor;

  useEffect(() => {
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);
  // Browser Back and app links ask through the same dialog as Previous/Next.
  const { navigate: go } = useLeaveGuard({
    active: dirty,
    onLeave: leave => setPending(() => leave),
  });

  // After a self-replace the old row stays on screen until the new one loads,
  // and after a save the row reloads. Until then the whole editor is
  // read-only, so nothing acts on the old row or is reseeded away.
  const itemMatches =
    !!item &&
    item.kind === kind &&
    item.id.toLowerCase() === itemId &&
    !editor.reloading;

  const refreshSilently = useCallback(
    (isCurrent: () => boolean) => loadItem({ silent: true, isCurrent }),
    [loadItem]
  );
  const matchPending = item?.order_match_pending === true;
  // Re-read while the fax is still arriving or its returned-order check is
  // pending. Arriving pauses for edits, since its pages reload; a check's
  // answer lands under them, as the editor keeps what the user changed.
  useProcessingPoll(
    itemMatches &&
      (item?.status === 'processing' || matchPending) &&
      (!dirty || matchPending) &&
      !busy &&
      !editor.saving &&
      !intakeOpen &&
      !confirmingRemove &&
      pending === null,
    refreshSilently
  );
  const originKey = navState.origin ?? urlKey;
  // Anything in flight that ends by leaving or reloading this fax.
  const editLocked =
    busy || editor.saving || editor.deciding || editor.archiving;
  const navLocked = editLocked || intakeOpen;
  const carry: ReviewNavState = { patientLabel: navState.patientLabel };
  const listPage = neighbors?.position ? pageOf(neighbors.position) : urlPage;
  const listUrl = withSearch('/fax-inbox', writeFilters(filters, listPage));
  const reviewUrl = (ref: FaxItemRef) =>
    withSearch(`/fax-inbox/${ref.kind}/${ref.id}`, search);

  const goTo = (ref: FaxItemRef) =>
    void go(reviewUrl(ref), { replace: true, state: carry });

  const goToList = () => void go(listUrl, { replace: true, state: carry });

  /** The fax has left the list: open the next one, else the list. */
  const advance = () => {
    const target = neighbors?.next ?? neighbors?.previous;
    if (target) goTo(target);
    else goToList();
  };

  /** Follow a commit to the row it landed on, keeping the reader's place. */
  const settleOn = async (ref: FaxItemRef) => {
    if (ref.kind !== kind || ref.id.toLowerCase() !== itemId) {
      await go(reviewUrl(ref), {
        replace: true,
        state: {
          ...carry,
          neighbors: neighbors ?? undefined,
          origin: originKey,
        },
      });
    } else {
      void loadItem();
    }
  };

  /** Run an action that leaves this fax, asking first about unsaved edits. */
  const guard = (action: () => void) => {
    if (dirty) setPending(() => action);
    else action();
  };

  const saveAndContinue = async () => {
    if (!itemMatches) return;
    const start = urlKey;
    const action = pending;
    const ref = await editor.commit();
    if (!ref) return;
    setPending(null);
    if (!stillAt(start)) return;
    // Settled first, so a leave runs from the row as saved.
    await settleOn(ref);
    action?.();
  };

  const discardAndContinue = () => {
    const action = pending;
    setPending(null);
    editor.discard();
    action?.();
  };

  const save = async () => {
    if (!itemMatches) return;
    const start = urlKey;
    const ref = await editor.commit();
    if (!ref) return;
    showSuccess('Saved.');
    if (stillAt(start)) void settleOn(ref);
  };

  const split = async () => {
    if (!itemMatches) return;
    const start = urlKey;
    const ref = await editor.split();
    if (!ref) return;
    showSuccess('Split into a new fax.');
    if (stillAt(start)) void settleOn(ref);
  };

  const fileToChart = async (
    patientId: string,
    request: Omit<RouteFaxRequest, 'patient_id' | 'fax_type_id'>
  ) => {
    if (!itemMatches || busy) return;
    const start = urlKey;
    setBusy(true);
    setFiling(true);
    try {
      const ref = await editor.commit();
      if (!ref || !stillAt(start)) return;
      // The URL moves to the committed row first, so a retry after a failed
      // filing routes that row rather than building a second document.
      await settleOn(ref);
      const result = await routeFaxItem(ref.kind, ref.id, {
        patient_id: patientId,
        fax_type_id: editor.typeId === NONE ? null : editor.typeId,
        ...request,
      });
      showSuccess(
        result.already_filed
          ? `Already filed to ${result.task_display_name}.`
          : `Filed to ${result.task_display_name}.`
      );
      if (stillAt(`${ref.kind}:${ref.id.toLowerCase()}`)) advance();
    } catch (error) {
      console.error('Failed to file the fax', error);
      // Not said over whatever the user has moved on to.
      if (currentUrl.current === null) return;
      if (getErrorConflictCode(error) === 'FAX_ALREADY_FILED') {
        showError('This fax is already filed to another task.');
      } else {
        showError(getBusinessErrorMessage(error) ?? 'Failed to file the fax.');
      }
    } finally {
      setBusy(false);
      setFiling(false);
    }
  };

  const remove = async () => {
    if (!item || !itemMatches) return;
    const start = urlKey;
    setBusy(true);
    try {
      await removeFaxItem(item.kind, item.id);
      setConfirmingRemove(false);
      showSuccess('Removed from the inbox.');
      if (stillAt(start)) advance();
    } catch (error) {
      console.error('Failed to remove the fax', error);
      if (stillAt(start)) showError('Could not remove that fax.');
    } finally {
      setBusy(false);
    }
  };

  const decideSignature = async (signed: boolean) => {
    if (!itemMatches) return;
    const start = urlKey;
    if ((await editor.decideSignature(signed)) && stillAt(start)) {
      void loadItem();
    }
  };

  const toggleArchive = async () => {
    if (!itemMatches) return;
    const start = urlKey;
    const message = await editor.toggleArchive();
    if (!message) return;
    showSuccess(message);
    if (stillAt(start)) advance();
  };

  const intakeStarted = (started: FaxItemIntakeStarted) => {
    setIntakeOpen(false);
    showSuccess('Started an intake.');
    if (!stillAt(urlKey)) return;
    // Replaced first, so browser Back from Intake Manager lands on the
    // document the intake was built from.
    // Both queue in order behind any step off the guard entry.
    void settleOn({ kind: 'document', id: started.fax_document_id });
    void go('/intake-manager');
  };

  if (!canManage) {
    return (
      <div>
        <p className='text-muted-foreground'>
          You do not have permission to view the fax inbox. Please contact your
          administrator if you need access.
        </p>
      </div>
    );
  }

  const backLink = (
    <Link
      to={listUrl}
      replace
      state={carry}
      {...LEAVE_GUARD_SKIP}
      aria-disabled={navLocked}
      tabIndex={navLocked ? -1 : undefined}
      className={`inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground${navLocked ? ' pointer-events-none opacity-50' : ''}`}
      onClick={event => {
        if (navLocked) {
          event.preventDefault();
          return;
        }
        if (!dirty) return;
        event.preventDefault();
        guard(goToList);
      }}
    >
      <ArrowLeft className='h-4 w-4' />
      Fax Inbox
    </Link>
  );

  // The last fax, still held after the URL moved on (not a self-replace). The
  // panel is keyed by the URL and seeds once, so it must not mount on it.
  const heldOver =
    !!item &&
    !selfReplace.current &&
    (item.kind !== kind || item.id.toLowerCase() !== itemId);
  if (loadState === 'missing' || loadState === 'failed' || !item || heldOver) {
    return (
      <div className='space-y-4'>
        {backLink}
        {loadState === 'loading' || heldOver ? (
          <div className='flex justify-center py-10'>
            <Loader2 className='h-6 w-6 animate-spin' />
          </div>
        ) : loadState === 'failed' ? (
          <div className='space-y-3'>
            <p className='text-destructive'>Could not load that fax.</p>
            <Button variant='outline' onClick={() => void loadItem()}>
              Try again
            </Button>
          </div>
        ) : (
          <p className='text-muted-foreground'>This fax is not in the inbox.</p>
        )}
      </div>
    );
  }

  const readOnly = !!item.chart_filed_at || item.filed;
  const pages =
    item.page_count === null
      ? null
      : item.page_count === 1
        ? '1 page'
        : `${item.page_count} pages`;

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
        <div className='min-w-0 space-y-1'>
          {backLink}
          <div className='flex flex-wrap items-center gap-2'>
            <h1 className='min-w-0 break-words text-xl font-semibold'>
              {displayName(item)}
            </h1>
            <span className='whitespace-nowrap'>
              <StatusBadge status={item.status} />
            </span>
            {item.filed && (
              <Badge variant='outline' className='whitespace-nowrap'>
                Merged
              </Badge>
            )}
            {item.chart_filed_at && (
              <Badge variant='outline' className='whitespace-nowrap'>
                Filed
              </Badge>
            )}
            {item.archived_at && (
              <Badge variant='outline' className='whitespace-nowrap'>
                Archived
              </Badge>
            )}
            <OrderBadge item={item} />
          </div>
          <div className='flex flex-wrap items-start gap-x-4 gap-y-1 text-sm text-muted-foreground'>
            <span className='flex gap-1'>
              <span>Sent by</span>
              <SenderLabel item={item} />
            </span>
            <span>Received {formatDateTimeInPT(item.received_at)}</span>
            {pages && <span>{pages}</span>}
          </div>
          {item.kind === 'arrival' && item.status === 'processing' && (
            <p className='text-sm text-muted-foreground'>
              Still arriving. The pages fill in once it is ready.
            </p>
          )}
        </div>
        <div className='flex flex-wrap items-center gap-2'>
          {neighbors && (
            <span className='whitespace-nowrap text-sm text-muted-foreground'>
              {neighbors.position
                ? `${neighbors.position} of ${neighbors.total}`
                : 'Not in this list'}
            </span>
          )}
          <Button
            variant='outline'
            size='sm'
            disabled={navLocked || !neighbors?.previous}
            onClick={() => {
              const target = neighbors?.previous;
              if (target) guard(() => goTo(target));
            }}
          >
            Previous
          </Button>
          <Button
            variant='outline'
            size='sm'
            disabled={navLocked || !neighbors?.next}
            onClick={() => {
              const target = neighbors?.next;
              if (target) guard(() => goTo(target));
            }}
          >
            Next
          </Button>
        </div>
      </div>

      <div className='flex flex-col gap-4 md:flex-row'>
        <section aria-label='Viewer' className='min-w-0 md:flex-[999_1_0%]'>
          <FaxPagesSection
            editor={editor}
            disabled={readOnly || !itemMatches || editLocked}
            onSplit={() => void split()}
          />
        </section>
        <aside className='w-full md:w-[400px] md:shrink-0'>
          <FaxRoutePanel
            key={originKey}
            item={item}
            editor={editor}
            itemMatches={itemMatches}
            busy={editLocked}
            filing={filing}
            canFile={rogerEmr}
            canIntake={canIntake}
            listUrl={listUrl}
            onSave={() => void save()}
            onFile={(patientId, request) =>
              void fileToChart(patientId, request)
            }
            onCreateIntake={() => guard(() => setIntakeOpen(true))}
            onRemove={() => setConfirmingRemove(true)}
            onDecideSignature={signed => void decideSignature(signed)}
            onToggleArchive={() => void toggleArchive()}
            onError={showError}
          />
        </aside>
      </div>

      {canIntake && kind && (
        <CreateIntakeDialog
          open={intakeOpen}
          item={item}
          itemRef={{ kind, id: itemId }}
          disabled={!itemMatches}
          onClose={() => setIntakeOpen(false)}
          onStarted={intakeStarted}
          onError={showError}
        />
      )}

      <ConfirmationDialog
        open={confirmingRemove}
        title='Remove this fax?'
        message={`${displayName(item)} will be taken out of the inbox. Its pages are kept and anything already built from it is unaffected, so support can put it back.`}
        confirmLabel='Remove'
        loading={busy}
        onAccept={() => void remove()}
        onCancel={() => setConfirmingRemove(false)}
      />

      <Dialog
        open={pending !== null}
        onOpenChange={open => !open && !editor.saving && setPending(null)}
      >
        <DialogContent className='max-w-md'>
          <DialogHeader>
            <DialogTitle>Save your changes?</DialogTitle>
          </DialogHeader>
          <p className='text-sm text-muted-foreground'>
            This fax has changes that are not saved yet.
          </p>
          <div className='flex flex-wrap justify-end gap-2'>
            <Button
              variant='outline'
              disabled={editor.saving}
              onClick={() => setPending(null)}
            >
              Cancel
            </Button>
            <Button
              variant='outline'
              disabled={editor.saving}
              onClick={discardAndContinue}
            >
              Discard
            </Button>
            <Button
              disabled={editor.saving || !itemMatches}
              onClick={() => void saveAndContinue()}
            >
              {editor.saving && (
                <Loader2 className='mr-2 h-4 w-4 animate-spin' />
              )}
              Save and continue
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
